import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { VersusScene } from '../components/mascot/scenes.js';
import { PageHero } from '../components/ui/hero.js';
import { WidgetShowcase } from '../components/WidgetShowcase.js';
import { breadcrumbSchema, faqSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';
import { track } from '../lib/analytics.js';
import {
  COMPETITORS,
  FIELD_LABELS,
  RETIRED_COMPARISONS,
  comparisonPath,
  competitorBySlug,
  oldestVerification,
  type Competitor,
} from '../content/competitors.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_COUNT_WORD,
} from '../content/widgets.js';
import { sampleSetForSource } from '../content/sample-sets.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';

/**
 * One comparison page.
 *
 * ===========================================================================
 * What replaced what
 * ===========================================================================
 * The outgoing site had seven of these. Each was a title, one paragraph and a
 * list of links to the other six — and the paragraph was the same paragraph
 * every time, asserting that "small and medium businesses prefer ReviewsJet
 * over X" about seven different companies, with nothing behind it. That is both
 * the thin content Google discounts and an unsupported claim about a named
 * business.
 *
 * This page is the opposite trade: fewer pages, each of which does the work.
 * Every competitor fact carries the URL it was read from and the date it was
 * checked, both shown to the reader, and every page names things the other
 * product does better. The "where they are stronger" section is not a
 * concession — it is the reason the rest of the page is believable.
 */
export const Route = createFileRoute('/compare/$slug')({
  loader: ({ params }) => {
    /*
      The four comparisons this rebuild does not carry forward are indexed
      URLs, so they get a permanent redirect to the hub rather than a 404.
      `/compare` is the right destination: it keeps the visitor inside the
      intent they searched with, and a 301 to a relevant hub passes on what
      the old page had accumulated. Redirecting them to `/` instead would read
      as a soft 404 to a crawler and as a dead end to a person.
    */
    if (RETIRED_COMPARISONS.some((entry) => entry.slug === params.slug)) {
      throw redirect({ to: '/compare', statusCode: 301 });
    }

    const competitor = competitorBySlug(params.slug);
    // Anything else unwritten 404s rather than rendering an empty shell, the
    // same rule the source × platform pages follow.
    if (!competitor) throw notFound();
    return { competitor };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { competitor } = loaderData;

    const { meta, links } = seo({
      title: `ReviewsJet vs ${competitor.name}`,
      description: `An honest comparison of ReviewsJet and ${competitor.name} — what each is built for, where each is stronger, and which one fits your case. Every fact sourced and dated.`,
      path: comparisonPath(competitor),
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Comparisons', path: '/compare' },
              {
                name: `vs ${competitor.name}`,
                path: comparisonPath(competitor),
              },
            ])
          ),
        },
        /*
          FAQ schema only where there are questions worth marking up, and
          never review schema: this page displays sample reviews to
          demonstrate a widget, and marking those as the page's own review
          ratings would be exactly the misrepresentation the brief forbids.
        */
        ...(competitor.faqs?.length
          ? [
              {
                type: 'application/ld+json',
                children: jsonLd(faqSchema(competitor.faqs)),
              },
            ]
          : []),
      ],
    };
  },
  component: ComparisonPage,
});

function ComparisonPage() {
  const { competitor } = Route.useLoaderData();

  return (
    <>
      <Hero competitor={competitor} />
      <QuickComparison competitor={competitor} />
      <Strengths competitor={competitor} />
      <ChooseEither competitor={competitor} />
      <Demonstration />
      <Faqs competitor={competitor} />
      <OtherComparisons current={competitor} />
      <Close competitor={competitor} />
    </>
  );
}

// ---------------------------------------------------------------------- Hero

function Hero({ competitor }: { competitor: Competitor }) {
  return (
    <PageHero
      trail={[
        { name: 'Home', path: '/' },
        { name: 'Comparisons', path: '/compare' },
        { name: `vs ${competitor.name}`, path: comparisonPath(competitor) },
      ]}
      art={<VersusScene name={competitor.name} />}
    >
      <Measure>
        <Eyebrow>Comparison</Eyebrow>
        <Heading level={1} size="hero" className="mt-4">
          ReviewsJet vs <Mark>{competitor.name}</Mark>
        </Heading>
        <Text size="lead" className="mt-6">
          {competitor.audience}
        </Text>
        <Text className="mt-4">
          {competitor.name} is {competitor.summary.charAt(0).toLowerCase()}
          {competitor.summary.slice(1)} ReviewsJet does something narrower:{' '}
          import the reviews a business already has, manage them in one place,
          and display them on its website.
        </Text>

        <p className="mt-6 text-caption text-ink-500">
          Every {competitor.name} fact below links to the page it was read from
          and shows when it was checked — last verified{' '}
          <time dateTime={oldestVerification(competitor)}>
            {formatDate(oldestVerification(competitor))}
          </time>
          . Pricing and features change; check the source before deciding.
        </p>
      </Measure>
    </PageHero>
  );
}

// ------------------------------------------------------------ Quick comparison

function QuickComparison({ competitor }: { competitor: Competitor }) {
  return (
    <Section tone="raised" aria-labelledby="quick-heading">
      <Measure>
        <Heading level={2} id="quick-heading">
          Side by side
        </Heading>
        <Text className="mt-4 max-w-prose">
          The dimensions that actually decide this, rather than a feature grid
          with ticks in every row on our side.
        </Text>
      </Measure>

      {/*
        A real table, because this is tabular data and a screen reader user
        deserves the row and column headers that make it navigable. The
        `overflow-x-auto` wrapper keeps the page body from scrolling sideways
        on a phone while the table itself still can.
      */}
      <div className="mt-10 overflow-x-auto">
        <table className="w-full min-w-[44rem] border-collapse text-left">
          <caption className="sr-only">
            ReviewsJet compared with {competitor.name}, by feature
          </caption>
          <thead>
            <tr className="border-b border-ink-200">
              <th scope="col" className="w-[9rem] py-3 pr-4 text-caption font-semibold text-ink-950">
                Dimension
              </th>
              <th scope="col" className="py-3 pr-4 text-caption font-semibold text-ink-950">
                ReviewsJet
              </th>
              <th scope="col" className="py-3 text-caption font-semibold text-ink-950">
                {competitor.name}
              </th>
            </tr>
          </thead>
          <tbody>
            {competitor.rows.map((row) => (
              <tr key={row.field} className="border-b border-ink-100 align-top">
                <th
                  scope="row"
                  className="py-4 pr-4 text-body-sm font-medium text-ink-950"
                >
                  {FIELD_LABELS[row.field]}
                </th>
                <td className="py-4 pr-4 text-body-sm text-ink-700">
                  {row.ours}
                </td>
                <td className="py-4 text-body-sm text-ink-700">
                  {row.theirs.value}
                  <a
                    href={row.theirs.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="mt-1.5 block text-caption text-ink-500 underline decoration-ink-200 underline-offset-[3px] hover:text-ink-800"
                  >
                    Source, checked {formatDate(row.theirs.verifiedOn)}
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ----------------------------------------------------------------- Strengths

function Strengths({ competitor }: { competitor: Competitor }) {
  return (
    <Section aria-labelledby="strengths-heading">
      <Heading level={2} id="strengths-heading" className="sr-only">
        Strengths of each
      </Heading>

      <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
        <div>
          <Heading level={3} size="title">
            Where ReviewsJet is stronger
          </Heading>
          <ul className="mt-6 space-y-4">
            {competitor.strengths.map((entry) => (
              <li key={entry} className="flex gap-3.5">
                <Tick />
                <span className="max-w-prose text-body-sm text-ink-700">
                  {entry}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Heading level={3} size="title">
            Where {competitor.name} is stronger
          </Heading>
          <ul className="mt-6 space-y-4">
            {competitor.theirStrengths.map((entry) => (
              <li key={entry} className="flex gap-3.5">
                <Tick muted />
                <span className="max-w-prose text-body-sm text-ink-700">
                  {entry}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-6 max-w-prose text-caption text-ink-500">
            This section is here because a comparison without one is an
            advertisement. If {competitor.name} is the right tool for what you
            are doing, it is better to find that out here than after paying us.
          </p>
        </div>
      </div>

      {competitor.workflowDifferences.length > 0 && (
        <div className="mt-14 border-t border-ink-100 pt-10">
          <Heading level={3} size="title">
            How the day-to-day differs
          </Heading>
          <ul className="mt-6 grid gap-x-10 gap-y-4 md:grid-cols-2 lg:grid-cols-3">
            {competitor.workflowDifferences.map((entry) => (
              <li
                key={entry}
                className="max-w-prose text-body-sm text-ink-600"
              >
                {entry}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Section>
  );
}

function Tick({ muted = false }: { muted?: boolean }) {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden
      className={
        muted
          ? 'mt-0.5 size-4 shrink-0 text-ink-400'
          : 'mt-0.5 size-4 shrink-0 text-accent-600'
      }
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 10.5l4 4 8-9" />
    </svg>
  );
}

// -------------------------------------------------------------- Choose either

function ChooseEither({ competitor }: { competitor: Competitor }) {
  return (
    <Section tone="raised" aria-labelledby="choose-heading">
      <Heading level={2} id="choose-heading" className="sr-only">
        Which to choose
      </Heading>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl bg-white p-6 ring-1 ring-ink-150 sm:p-8">
          <Heading level={3} size="title">
            Choose ReviewsJet if&hellip;
          </Heading>
          <p className="mt-4 max-w-prose text-body text-ink-700">
            {competitor.bestForUs}
          </p>
          <div className="mt-7">
            <ButtonLink to={WIDGET_BUILDER_PATH} size="md">
              Build a widget free
            </ButtonLink>
          </div>
        </div>

        <div className="rounded-xl bg-white p-6 ring-1 ring-ink-150 sm:p-8">
          <Heading level={3} size="title">
            Choose {competitor.name} if&hellip;
          </Heading>
          <p className="mt-4 max-w-prose text-body text-ink-700">
            {competitor.bestForThem}
          </p>
          <p className="mt-7">
            <a
              href={competitor.homepage}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-body-sm text-ink-600 underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950"
            >
              Visit {competitor.name}
            </a>
          </p>
        </div>
      </div>
    </Section>
  );
}

// ------------------------------------------------------------ Demonstration

function Demonstration() {
  return (
    <Section aria-labelledby="demo-heading">
      <Measure>
        <Heading level={2} id="demo-heading">
          What the ReviewsJet workflow looks like
        </Heading>
        <Text className="mt-4 max-w-prose">
          Paste a listing, publish the reviews worth showing, style the widget,
          paste one line into the site. This is the widget at the end of it —
          the real renderer, with sample reviews.
        </Text>
      </Measure>

      <ol className="mt-10 grid gap-6 md:grid-cols-4">
        {[
          'Paste your Google, Trustpilot, Yelp, Tripadvisor or Airbnb listing, or your App Store or Google Play page.',
          'Publish the ones worth showing. Nothing goes live until you say so.',
          'Style it to your site — colours, border, radius, what each card shows.',
          'Paste one line, or install the Wix, Webflow or WordPress app.',
        ].map((step, index) => (
          <li key={step} className="flex gap-3">
            <span className="nums text-body-sm font-semibold text-ink-300">
              {String(index + 1).padStart(2, '0')}
            </span>
            <span className="max-w-prose text-body-sm text-ink-600">{step}</span>
          </li>
        ))}
      </ol>

      <div className="rj-canvas mt-10 rounded-xl p-4 ring-1 ring-ink-150 sm:p-8">
        <WidgetShowcase
          type="masonry"
          reviews={sampleSetForSource('google').reviews.slice(0, 6)}
        />
      </div>

      <p className="mt-4 text-caption text-ink-500">
        Sample reviews, shown to demonstrate the widget. They are not ReviewsJet
        customer reviews and are not marked up as ratings for this page.
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <ButtonLink to={WIDGET_BUILDER_PATH} size="lg">
          Try it with your own reviews
        </ButtonLink>
        <ButtonLink to="/widgets" variant="secondary" size="lg">
          See all {WIDGET_COUNT_WORD} layouts
        </ButtonLink>
      </div>
    </Section>
  );
}

// ----------------------------------------------------------------------- FAQ

/**
 * The questions, rendered.
 *
 * Paired with the FAQ schema in `head` and not optional when that schema is
 * emitted: structured data describing questions a visitor cannot find on the
 * page is the misuse that gets rich results revoked. Both read the same array.
 */
function Faqs({ competitor }: { competitor: Competitor }) {
  if (!competitor.faqs?.length) return null;

  return (
    <Section tone="raised" aria-labelledby="faq-heading">
      <Measure>
        <Heading level={2} id="faq-heading">
          Questions people ask about this.
        </Heading>
      </Measure>

      <dl className="mt-10 max-w-text">
        {competitor.faqs.map((faq, index) => (
          <div
            key={faq.q}
            className={index > 0 ? 'mt-8 border-t border-ink-150 pt-8' : ''}
          >
            <dt className="text-title text-ink-950">{faq.q}</dt>
            <dd className="mt-2.5 text-body text-ink-600">{faq.a}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

// -------------------------------------------------------- Other comparisons

function OtherComparisons({ current }: { current: Competitor }) {
  const others = COMPETITORS.filter((entry) => entry.slug !== current.slug);
  if (others.length === 0) return null;

  return (
    <Section tone="raised" aria-labelledby="others-heading">
      <Heading level={2} id="others-heading" size="title">
        Other comparisons
      </Heading>
      <ul className="mt-5 flex flex-wrap gap-x-6 gap-y-3">
        {others.map((entry) => (
          <li key={entry.slug}>
            <SiteLink
              to={comparisonPath(entry)}
              className="text-body-sm text-ink-600 underline decoration-ink-200 underline-offset-[3px] hover:text-ink-950"
            >
              ReviewsJet vs {entry.name}
            </SiteLink>
          </li>
        ))}
      </ul>
    </Section>
  );
}

// --------------------------------------------------------------------- Close

function Close({ competitor }: { competitor: Competitor }) {
  return (
    <Section tone="ink" size="base">
      <Measure>
        <Heading level={2} className="text-white">
          Try the part {competitor.name} makes you pay for.
        </Heading>
        <Text className="mt-4 text-white/70">
          Every layout is on the free plan, there is no view limit, and you can
          build a widget before making an account.
        </Text>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
          <ButtonLink
            to={WIDGET_BUILDER_PATH}
            variant="inverse"
            size="lg"
            onClick={() =>
              track('comparison_cta_clicked', {
                competitor: competitor.slug,
                surface: 'comparison-close',
              })
            }
          >
            Build a widget free
          </ButtonLink>
          <ButtonAnchor
            href={startUrl('comparison-signup')}
            variant="inverse-ghost"
            size="lg"
            onClick={() =>
              track('comparison_cta_clicked', {
                competitor: competitor.slug,
                surface: 'comparison-signup',
              })
            }
          >
            Start free
          </ButtonAnchor>
        </div>
      </Measure>
    </Section>
  );
}

/** "18 August 2026" — spelled out, because 08/09 reads two ways. */
function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
